/**
 * web-sign-in-checkout D8 — the held time on the website session (the app's
 * SessionRecoveryGuard, without Telegram): when it lapses, a warning two
 * minutes before with Keep (extend-hold, 15 more minutes), and after it
 * lapsed Get my time back (recover-hold): held again, or taken (pick a new
 * time on the offering's page). Checked again when the tab returns to view,
 * since a background tab's timers are slowed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WebAppFlowContextResponse } from '@/api/types';

import { useStripeReturnPayment } from '@/lib/stripeReturn';

import { SiteFetchError, webFetch } from '../api/session';
import { usePageMover } from '../pageMover';
import { flowKeys, webFlow } from './useWebFlow';

const WARN_WINDOW_MS = 2 * 60_000;
const HEARTBEAT_MS = 5_000;
const REHELD_MS = 1_500;
/** setTimeout's longest delay (a signed 32-bit count of milliseconds). */
const MAX_TIMER_MS = 2_147_000_000;
/** Keep's answers that mean the time already ran out, as the app's guard reads them: offer Get my time back. */
const LAPSED_CODES = new Set(['HOLD_EXPIRED', 'NO_ACTIVE_RESERVATION']);

export type HoldPhase = 'alive' | 'warn' | 'prompt' | 'reheld' | 'taken';

export interface WebHold {
  /** The held time's end, ISO; null when nothing is held. */
  readonly expiresAt: string | null;
  readonly phase: HoldPhase;
  readonly keeping: boolean;
  readonly keepFailed: boolean;
  readonly recovering: boolean;
  readonly recoverError: string | null;
  /** Counts each time the time was held again; a card form started before it is for the old hold. */
  readonly attempt: number;
  readonly keep: () => void;
  readonly recover: () => void;
  readonly repick: () => void;
}

export function useWebHold(
  flowToken: string | null,
  flow: WebAppFlowContextResponse | undefined,
  offeringPath: string | null,
): WebHold {
  const queryClient = useQueryClient();
  const navigate = usePageMover();
  const f = flow?.flow;
  // A booking already made holds nothing.
  const expiresAt = f && !f.booking ? (f.placeholderExpiresAt ?? f.holdExpiresAt ?? null) : null;
  const lapsedOnServer = Boolean(f && !f.booking && (f.placeholderExpired || f.holdExpired));
  // Back from a Stripe hand-off, the held time may already be the booking: no prompt or warning until its payment is read.
  const returning = useStripeReturnPayment() !== null;

  const [now, setNow] = useState(() => Date.now());
  const [recovery, setRecovery] = useState<'none' | 'reheld' | 'taken'>('none');
  const [keeping, setKeeping] = useState(false);
  const [keepFailed, setKeepFailed] = useState(false);
  const [keepRefused, setKeepRefused] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setNow(Date.now());
    const timer = setInterval(tick, HEARTBEAT_MS);
    const until = new Date(expiresAt).getTime() - Date.now();
    const boundary = until > 0 ? setTimeout(tick, Math.min(until + 50, MAX_TIMER_MS)) : undefined;
    const warnAt = until - WARN_WINDOW_MS;
    const warn = warnAt > 0 ? setTimeout(tick, Math.min(warnAt + 50, MAX_TIMER_MS)) : undefined;
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      if (boundary) clearTimeout(boundary);
      if (warn) clearTimeout(warn);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [expiresAt]);

  const left = expiresAt ? new Date(expiresAt).getTime() - now : Infinity;
  const phase: HoldPhase =
    recovery !== 'none'
      ? recovery
      : returning
        ? 'alive'
        : lapsedOnServer || keepRefused || left <= 0
          ? 'prompt'
          : left <= WARN_WINDOW_MS
            ? 'warn'
            : 'alive';

  const refresh = useCallback(
    () => (flowToken ? queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) }) : Promise.resolve()),
    [flowToken, queryClient],
  );

  const keep = useCallback(() => {
    if (!flowToken || keeping) return;
    setKeeping(true);
    setKeepFailed(false);
    webFetch<{ expiresAt: string }>(`/flow/${encodeURIComponent(flowToken)}/extend-hold`, { method: 'POST', body: '{}' })
      .then(() => refresh())
      .catch((err: unknown) => {
        if (err instanceof SiteFetchError && LAPSED_CODES.has(err.code ?? '')) setKeepRefused(true);
        else setKeepFailed(true);
      })
      .finally(() => setKeeping(false));
  }, [flowToken, keeping, refresh]);

  // Pick a new time: a service starts over on its taken time, so the checkout shows its next openings
  // (spec: a lapsed hold offers the next openings when it is not free); an event or a series returns to its page.
  const serviceId = f && !f.selectedEventId && !f.selectedSeriesId ? (flow?.context.service?.id ?? null) : null;
  const takenStart = f?.selectedSlot?.startsAt ?? null;
  // Read when the cancel lands, not when the button was drawn: the offering's page may load after the prompt shows.
  const offeringPathNow = useRef(offeringPath);
  offeringPathNow.current = offeringPath;
  const repick = useCallback(() => {
    if (!flowToken) return;
    void webFlow
      .cancel(flowToken)
      .catch(() => undefined)
      .finally(() => {
        queryClient.removeQueries({ queryKey: flowKeys.context(flowToken) });
        setRecovery('none');
        if (serviceId && takenStart) navigate(`/checkout?${new URLSearchParams({ service: serviceId, start: takenStart })}`, { replace: true });
        else navigate(offeringPathNow.current ?? '/', { replace: true });
      });
  }, [flowToken, queryClient, navigate, serviceId, takenStart]);

  const recover = useCallback(() => {
    if (!flowToken || recovering) return;
    setRecovering(true);
    setRecoverError(null);
    webFetch<{ status: 'reheld' | 'taken' }>(`/flow/${encodeURIComponent(flowToken)}/recover-hold`, { method: 'POST', body: '{}' })
      .then(async (res) => {
        if (res.status === 'taken') {
          setRecovery('taken');
          return;
        }
        setRecovery('reheld');
        setKeepRefused(false);
        setAttempt((a) => a + 1);
        await refresh();
        setNow(Date.now());
        setTimeout(() => setRecovery('none'), REHELD_MS);
      })
      .catch((err: unknown) => {
        // A series' time is never got back (the server says so every time): as the app does, the dead
        // checkout ends and the series' page opens, instead of a Try again that cannot work (Codex review 2026-10-26).
        if (err instanceof SiteFetchError && err.code === 'UNSUPPORTED_RECOVERY') {
          repick();
          return;
        }
        setRecoverError("Couldn't get it back. Please try again.");
      })
      .finally(() => setRecovering(false));
  }, [flowToken, recovering, refresh, repick]);

  return { expiresAt, phase, keeping, keepFailed, recovering, recoverError, attempt, keep, recover, repick };
}
